/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React base styles
import breakpoints from "assets/theme/base/breakpoints";

// Images
import burceMars from "assets/images/bruce-mars.jpg";
import backgroundImage from "assets/images/bg-profile.jpg";
import { useSelector } from "react-redux";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import { deleteUser } from "services/apis";
import { useNavigate } from "react-router-dom";
function Header({ children }) {
  const { userDetail: user } = useSelector((state) => state.userDetail)
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate()

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  const handleDeleteUser = async (userId) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Are you sure you want to delete this User?",
      html: '<p style="color: red;">Deleting this User will remove all related data. <strong>This action cannot be undone.</strong></p>',
      showDenyButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
    });

    if (result.isConfirmed) {
      try {
        await deleteUser({ userId });
        await Swal.fire({
          icon: "success",
          title: "User Deleted",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/users");
      } catch (error) {
        console.error("Error deleting user:", error);

        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: error?.message || "There was an error deleting this User.",
        });
      }
    }
  };

  return (
    <MDBox position="relative" mb={5}>
      <MDBox
        display="flex"
        alignItems="left"
        justifyContent="left"
        position="relative"
        minHeight="18.75rem"
        borderRadius="xl"
        sx={{
          backgroundImage: ({
            functions: { rgba, linearGradient },
            palette: { gradients },
          }) =>
            `  url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
        }}
      >
        <MDBox
          position="absolute"
          top="3%"
          left="1.5%"
          transform="translate(-50%, -50%)"
          textAlign="left"
          sx={{
            color: "rgba(245, 245, 253, 0.4)",
            fontSize: "2.5rem",
            zIndex: 1,
          }}
        >
          User Profile
        </MDBox>
      </MDBox>
      <Card
        sx={{
          position: "relative",
          mt: -8,
          mx: 3,
          py: 2,
          px: 2,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <MDAvatar src={user?.userProfiles?.[0]?.image} alt="profile-image" size="xl" shadow="sm" />
          </Grid>
          <Grid item>
            <MDBox height="100%" mt={0.5} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                {user?.name}
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="regular">
                {user?.email}
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4} mr={5} sx={{ display: "flex", justifyContent: "flex-end", ml: "auto" }}>
            <MDButton
              onClick={() => handleDeleteUser(user.userId)}
              sx={{
                backgroundImage: `linear-gradient(90deg, #e01212 0%, #e01212 100%)`,
                width: { xs: "100%", sm: "auto" },
              }}
              size="small"
            >
              <MDTypography
                sx={{ fontSize: "13.5px", fontWeight: "bold" }}
                color="white"
              >
                Delete User
              </MDTypography>
            </MDButton>
          </Grid>
        </Grid>
        {children}
      </Card>
    </MDBox>
  );
}

// Setting default props for the Header
Header.defaultProps = {
  children: "",
};

// Typechecking props for the Header
Header.propTypes = {
  children: PropTypes.node,
};

export default Header;
